import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDeepCompareEffect } from 'react-use';
import { MultiSelect } from '../../../components/other/MultiSelect';
import PageLoader from '../../../components/other/PageLoader';
import Layout from '../../../layout/default';
import { getUserSubmissions, exportUserSubmissions } from '../../../services/folder/form-avaliable';
import { localToUTCStringForDateRange } from '../../../utility/dates/localToUTCForDateRange';
import { utcToLocalDateFormatter } from '../../../utility/dates/utcToLocalDate';
import { sanitizePDFName } from '../../../utility/files/sanitizePDFName';
import './styles.scss';
import SelectFormsModal from './SelectFormsModal';
import { ReactComponent as XIcon } from '../../../assets/images/x-circle-black.svg';
import DateCalendar from '../../../components/calendars/DateCalendar/DateCalendar';

const ExportUserSubmissions = () => {
  const { id, user_id } = useParams();
  const navigate = useNavigate();

  const pageBreadcrumbs = [
    {
      url: `/company/${id}/user/${user_id}`,
      name: 'User',
    },
    {
      url: `/company/${id}/user/${user_id}/forms`,
      name: `Generate the PDF`,
    },
  ];

  const submissionsTableCols = [
    {
      name: 'Submission',
    },
    {
      name: 'Status',
    },
    {
      name: 'Initiation date',
    },
    {
      name: 'Submission date',
    },
  ];

  const initialFormData = {
    pdfName: '',
    forms: [],
    submittedDate: [null, null],
    initiatedDate: [null, null],
  };

  const MAX_SELECTED_SUBMISSIONS = 30;

  const [formData, setFormData] = useState(initialFormData);
  const [applyDatesToInitiation, setApplyDatesToInitiation] = useState(false);

  const [submissionsSearch, setSubmissionsSearch] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [isSubmissionsLoading, setIsSubmissionsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSelectFormsModalOpen, setIsSelectFormsModalOpen] = useState(false);

  const isValidPDFName = useMemo(() => {
    const trimValue = formData.pdfName.trim();
    return trimValue.length > 0 && trimValue.length <= 255;
  }, [formData.pdfName]);

  useDeepCompareEffect(() => {
    if (formData.forms.length === 0) {
      setSubmissions([]);
      return;
    }

    const initiationDateRange = applyDatesToInitiation ? formData.submittedDate : formData.initiatedDate;

    const params = {
      forms_ids: formData.forms.map((item) => item.id),
      submission_date_after: formData.submittedDate[0] ? localToUTCStringForDateRange(formData.submittedDate[0], 'start') : null,
      submission_date_before: formData.submittedDate[1] ? localToUTCStringForDateRange(formData.submittedDate[1], 'end') : null,
      initiation_date_after: initiationDateRange[0] ? localToUTCStringForDateRange(initiationDateRange[0], 'start') : null,
      initiation_date_before: initiationDateRange[1] ? localToUTCStringForDateRange(initiationDateRange[1], 'end') : null,
    };
    setIsSubmissionsLoading(true);

    getUserSubmissions(user_id, params)
      .then((response) => {
        setSubmissions(response.data);
        setIsSubmissionsLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setIsSubmissionsLoading(false);
      });
  }, [setIsSubmissionsLoading, formData.forms, formData.submittedDate, formData.initiatedDate, applyDatesToInitiation]);

  const handleFormChange = (key, value) => {
    setFormData((prevState) => {
      const newState = _.cloneDeep(prevState);
      _.set(newState, key, value);
      return newState;
    });
  };

  const displayingSubmissions = useMemo(() => {
    if (!submissionsSearch) {
      return submissions;
    }

    return submissions.filter((submission) => submission.name.toLowerCase().indexOf(submissionsSearch.toLowerCase().trim()) !== -1);
  }, [submissionsSearch, submissions]);

  const isFormDataValid = useMemo(() => {
    return !!isValidPDFName && selectedSubmissions.length > 0 && !isGenerating;
  }, [selectedSubmissions, isValidPDFName, isGenerating]);

  const handleSelectSubmission = (id, isChecked) => {
    if (isChecked) {
      setSelectedSubmissions((state) => state.filter((itemID) => itemID !== id));
    } else {
      setSelectedSubmissions((state) => {
        if (state.length >= MAX_SELECTED_SUBMISSIONS) return state;
        return [...state, id];
      });
    }
  };

  const isAllSelected = useMemo(() => {
    if (
      displayingSubmissions.length > 0 &&
      displayingSubmissions.every((item) => selectedSubmissions.some((selectedSubmission) => selectedSubmission === item.id))
    ) {
      return true;
    }

    return false;
  }, [selectedSubmissions, displayingSubmissions]);

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedSubmissions((state) =>
        state.filter((item) => !displayingSubmissions.some((displayingSubmission) => displayingSubmission.id === item)),
      );
    } else {
      setSelectedSubmissions((state) => {
        const merged = [...new Set([...state, ...displayingSubmissions.map((item) => item.id)])];
        return merged.slice(0, MAX_SELECTED_SUBMISSIONS);
      });
    }
  };

  const triggerBrowserDownload = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    if (isGenerating) return;

    setIsGenerating(true);
    try {
      const body = {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        submission_parts_ids: selectedSubmissions.filter((submission) => displayingSubmissions.some((item) => item.id === submission)),
        file_name: formData.pdfName.trim(),
      };

      const response = await exportUserSubmissions(user_id, body);
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      triggerBrowserDownload(pdfBlob, `${formData.pdfName.trim()}.pdf`);
    } catch (e) {
      console.error(e);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClearForm = () => {
    setFormData(initialFormData);
    setApplyDatesToInitiation(false);
  };

  const handleClosePage = () => {
    navigate(`/company/${id}/user/${user_id}/forms`);
  };

  return (
    <Layout breadcrumbs={pageBreadcrumbs} title={'Generate the PDF'}>
      {isSelectFormsModalOpen && (
        <SelectFormsModal
          formData={formData}
          handleFormChange={handleFormChange}
          onClose={() => setIsSelectFormsModalOpen(false)}
          companyID={id}
          userID={user_id}
        />
      )}
      <div style={{ width: '100%' }} className="export-pdf-wrapper">
        <div className={'report-builder'} style={{ height: '100%' }}>
          <div className={'report-builder-content'} style={{ position: 'relative' }}>
            {isSubmissionsLoading && <PageLoader />}

            <h3 className="select-submissions-title">Select Submissions</h3>
            <div className="library-filter">
              <div className="library-filter-search form-control-item">
                <input
                  value={submissionsSearch}
                  onChange={(e) => {
                    setSubmissionsSearch(e.target.value);
                  }}
                  type="text"
                  className="form-control"
                  placeholder="Search"
                />
                <i className="icon-search"></i>
              </div>
            </div>

            {formData.forms.length ? (
              <div className="table-responsive table-default table-full">
                <div className="table-wrapper">
                  <div className="table">
                    <div className="thead">
                      <div className="tr">
                        <div className="th th-checkbox">
                          <div className="form-control-checkbox d-flex">
                            <div>
                              <input type="checkbox" onChange={handleSelectAll} checked={isAllSelected} />
                              <span className={''}></span>
                            </div>
                            <strong className="select-all-label">All</strong>
                          </div>
                        </div>
                        {submissionsTableCols.map((item, index) => (
                          <div className="th" key={index}>
                            {item.name}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="tbody">
                      {displayingSubmissions.map((item) => {
                        const isChecked = selectedSubmissions.some((id) => id === item.id);

                        return (
                          <div className="tr cursor-pointer" key={item.id} onClick={() => handleSelectSubmission(item.id, isChecked)}>
                            <div className="td td-checkbox">
                              <div className="form-control-checkbox d-flex">
                                <input type="checkbox" onChange={() => {}} checked={isChecked} />
                                <span className={''}></span>
                              </div>
                            </div>
                            <div className="td">{item.name}</div>
                            <div className="td">{item.status}</div>
                            <div className="td">{item.initiated_at ? utcToLocalDateFormatter(item.initiated_at) : ''}</div>
                            <div className="td">{item.submitted_at ? utcToLocalDateFormatter(item.submitted_at) : ''}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <h3 className="to-view-list-title mt-4 text-center">To view a list of submissions please select forms</h3>
            )}
          </div>

          <aside className={'report-builder-aside'}>
            <div className="d-flex justify-content-space-between generate-pdf-block">
              <h2 className="title d-flex justify-space-between">Generate the PDF</h2>
              <div className="d-flex align-items-center">
                <button onClick={handleClearForm} className="btn btn-outline m-0">
                  <XIcon className="mr-2" />
                  Clear
                </button>
                <button onClick={handleClosePage} className="btn-close-page" type="button" aria-label="Close">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className={'form-item mb-6'}>
              <label className={'form-label'}>Specify the PDF name</label>
              <div className={'form-item-wrapper'}>
                <input
                  value={formData.pdfName}
                  type="text"
                  className={'form-control'}
                  placeholder={'Select...'}
                  onChange={(e) => handleFormChange('pdfName', sanitizePDFName(e.target.value))}
                />
              </div>
              {!isValidPDFName && formData.pdfName.trim() && (
                <span className="form-error">File name can't be longer than 255 characters</span>
              )}
            </div>

            <div className="form-item mb-6">
              <label className="form-label">Select Forms</label>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSelectFormsModalOpen(true);
                }}
              >
                <MultiSelect
                  options={[]}
                  placeholder="Select"
                  allName="All"
                  otherSelectProps={{
                    menuIsOpen: false,
                    isDisabled: true,
                    styles: {
                      control: (base, state) => ({
                        ...base,
                        backgroundColor: 'unset',
                      }),
                    },
                  }}
                ></MultiSelect>
              </div>

              {formData.forms.length > 0 && (
                <div className="selected--forms">
                  {formData.forms.map((item) => {
                    return (
                      <div className="selected--forms__list__item" key={item.id}>
                        <p>{item.name}</p>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          onClick={() =>
                            handleFormChange(
                              'forms',
                              formData.forms.filter((form) => item.id !== form.id),
                            )
                          }
                        >
                          <path
                            d="M12.5003 7.4974L7.50033 12.4974M7.50033 7.4974L12.5003 12.4974M18.3337 9.9974C18.3337 14.5998 14.6027 18.3307 10.0003 18.3307C5.39795 18.3307 1.66699 14.5998 1.66699 9.9974C1.66699 5.39502 5.39795 1.66406 10.0003 1.66406C14.6027 1.66406 18.3337 5.39502 18.3337 9.9974Z"
                            stroke="#FDA29B"
                            strokeWidth="1.67"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className={'form-item mb-6'}>
              <label className={'form-label'}>Period of submitting</label>
              <DateCalendar
                isClearable
                selectsRange
                selectedDate={formData.submittedDate[0]}
                startDate={formData.submittedDate[0]}
                endDate={formData.submittedDate[1]}
                placeholderText="Select"
                handleChange={(e) => handleFormChange('submittedDate', e)}
              />
            </div>
            <div className={'form-item mb-6'}>
              <div className="form-control-checkbox d-flex cursor-pointer" onClick={() => setApplyDatesToInitiation((prev) => !prev)}>
                <div>
                  <input type="checkbox" onChange={() => {}} checked={applyDatesToInitiation} />
                  <span className={''}></span>
                </div>
                <label className="apply-dates-label">Apply the selected dates to the &ldquo;Period of form initiation&rdquo;</label>
              </div>
            </div>
            <button onClick={handleSubmit} disabled={!isFormDataValid} className={'btn'}>
              <svg className={'mr-2'} xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                <g clipPath="url(#clip0_5076_1432)">
                  <path
                    d="M19.6668 3.33136V8.33136M19.6668 8.33136H14.6668M19.6668 8.33136L15.8002 4.69803C14.9045 3.80196 13.7965 3.14737 12.5795 2.79534C11.3625 2.44331 10.0761 2.40532 8.8404 2.68491C7.60472 2.9645 6.46 3.55256 5.51305 4.39421C4.56611 5.23586 3.8478 6.30368 3.42516 7.49803M1.3335 16.6647V11.6647M1.3335 11.6647H6.3335M1.3335 11.6647L5.20016 15.298C6.09579 16.1941 7.20381 16.8487 8.42084 17.2007C9.63787 17.5527 10.9242 17.5907 12.1599 17.3111C13.3956 17.0316 14.5403 16.4435 15.4873 15.6018C16.4342 14.7602 17.1525 13.6924 17.5752 12.498"
                    stroke="white"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_5076_1432">
                    <rect width="20" height="20" fill="white" transform="translate(0.5)" />
                  </clipPath>
                </defs>
              </svg>
              {isGenerating ? 'Generating...' : 'Generate PDF'}
            </button>
            <div className="pdf-info-hint d-flex">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="32" height="32" rx="16" fill="#F4EBFF" />
                <rect x="2" y="2" width="32" height="32" rx="16" stroke="#F9F5FF" strokeWidth="4" />
                <path
                  d="M18 25.3496C13.9407 25.3496 10.6504 22.0593 10.6504 18C10.6504 13.9407 13.9407 10.6504 18 10.6504C22.0593 10.6504 25.3496 13.9407 25.3496 18C25.3496 22.0593 22.0593 25.3496 18 25.3496ZM18 15.4502C18.359 15.4502 18.6504 15.1588 18.6504 14.7998C18.6503 14.4409 18.3589 14.1504 18 14.1504H17.9922C17.6333 14.1504 17.3419 14.4409 17.3418 14.7998C17.3418 15.1588 17.6332 15.4502 17.9922 15.4502H18ZM18 21.8496C18.3589 21.8496 18.6503 21.5591 18.6504 21.2002V18C18.6504 17.641 18.359 17.3496 18 17.3496C17.641 17.3496 17.3496 17.641 17.3496 18V21.2002C17.3497 21.5591 17.6411 21.8496 18 21.8496Z"
                  stroke="#7F56D9"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <span className="pdf-info-hint__label">Large PDF file generation might take time.</span>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
};

export default ExportUserSubmissions;
